/**
 * Billing: the native eMerge ledger on the resident's dossier (2026-10-20). This is the live balance. Monday is
 * retired; its figures stay below as a dated mirror and are never added to these. Finance records invoices, payments,
 * opening balances, voids and "Paid in full — close" here; each act opens a small inline form with a plain
 * confirmation line, carries one Idempotency-Key per opening (reused on a retry), and the server decides again.
 */
import React, { useRef, useState } from 'react';
import { shortDate, freshKey, ResidentContext, FinanceScope } from './financeApi';
import {
  ledgerApi, writeFailure, parseDollars, centsToDollars, centsToField, todayEastern,
  AvailableLedger, Charge, Payment, PaymentMethod, ApiResultLike,
} from './ledgerApi';

const DEFAULT_METHODS: { id: PaymentMethod; label: string }[] = [
  { id: 'CASH', label: 'Cash' }, { id: 'CHECK', label: 'Check' }, { id: 'CARD', label: 'Card' }, { id: 'ACH', label: 'ACH / bank transfer' },
  { id: 'MONEY_ORDER', label: 'Money order' }, { id: 'FACILITY', label: 'Paid by the facility' }, { id: 'OTHER', label: 'Other' },
];

/** The warning that goes with every Monday figure ("Monday historical mirror, as of Oct 14, 2026 ... not the live balance"). */
export function MondayWarning({ text }: { text: string }) {
  return <p className="lg-warning" role="note"><strong>Monday figures are history.</strong> {text}</p>;
}

/** A swatch beside the word, so a state is never colour alone. `kind`: which states to show. */
export function StateLegend({ items, label }: { items: { key: string; word: string }[]; label: string }) {
  return (
    <ul className="lg-legend" aria-label={label}>
      {items.map((i) => <li key={i.key}><span className={`lg-swatch lg-s-${i.key}`} aria-hidden="true" /> {i.word}</li>)}
    </ul>
  );
}
export const CHARGE_LEGEND = [{ key: 'open', word: 'Open: money still owed' }, { key: 'paid', word: 'Paid' }, { key: 'void', word: 'Void: cancelled' }];
export const PAYMENT_LEGEND = [{ key: 'recorded', word: 'Recorded' }, { key: 'voided', word: 'Voided: cancelled' }];

const CHARGE_WORD: Record<Charge['state'], string> = { OPEN: 'Open', PAID: 'Paid', VOID: 'Void' };
const chargeName = (c: { kind: string; invoice_number: string | null }) => (c.kind === 'OPENING_BALANCE' ? (c.invoice_number ? `Opening balance ${c.invoice_number}` : 'Opening balance') : (c.invoice_number || 'Invoice'));
const dayWords = (iso: string) => shortDate(iso);

type Open =
  | { k: 'invoice' } | { k: 'opening' } | { k: 'payment' } | { k: 'close' }
  | { k: 'void-invoice'; charge: Charge } | { k: 'void-payment'; payment: Payment };

type Built = { error: string } | { call: (key: string, confirmDuplicate: boolean) => Promise<ApiResultLike>; success: (data: any) => string };

/**
 * One inline form. The key is made when the form opens and kept for retries; only IDEMPOTENCY_KEY_REUSED spends it.
 * A POSSIBLE_DUPLICATE answer shows the server's sentence and a box; ticking it resends with confirm_duplicate on the same key.
 */
function FormShell({ label, confirmText, submitLabel, busyLabel, build, onClose, onDone, children }: {
  label: string; confirmText: string | null; submitLabel: string; busyLabel: string; build: () => Built;
  onClose: () => void; onDone: (msg: string) => Promise<void> | void; children: React.ReactNode;
}) {
  const key = useRef<string>(freshKey());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dup, setDup] = useState(false);
  const [dupOk, setDupOk] = useState(false);
  return (
    <form className="um-form lg-form" aria-label={label} onSubmit={async (e) => {
      e.preventDefault();
      if (busy) return;
      const b = build();
      if ('error' in b) { setErr(b.error); return; }
      setBusy(true); setErr(null);
      try {
        const r = await b.call(key.current, dup && dupOk);
        if (r && r.success) { await onDone(b.success(r.data)); return; }
        const f = writeFailure(r as any);
        if (f.restart) key.current = freshKey();
        if (f.duplicate) { setDup(true); setDupOk(false); }
        setErr(f.message);
      } catch {
        setErr('Could not reach the server; it may or may not have been recorded. Try again: the same submission will not be recorded twice.');
      } finally { setBusy(false); }
    }}>
      {children}
      {confirmText && <p className="lg-confirm" role="note">{confirmText}</p>}
      {dup && (
        <label className="lg-check"><input type="checkbox" checked={dupOk} onChange={(e) => setDupOk(e.target.checked)} /> <span>Yes, this is a second, separate payment</span></label>
      )}
      <div className="btns">
        <button type="submit" className="um-open um-primary" disabled={busy || (dup && !dupOk)}>{busy ? busyLabel : submitLabel}</button>
        <button type="button" className="um-open" onClick={onClose} disabled={busy}>Cancel</button>
      </div>
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </form>
  );
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => <label><span>{label}</span>{children}</label>;

function InvoiceForm({ ctx, onClose, onDone }: { ctx: ResidentContext; onClose: () => void; onDone: (m: string) => Promise<void> | void }) {
  const [number, setNumber] = useState('');
  const [issued, setIssued] = useState(todayEastern());
  const [due, setDue] = useState('');
  const [amount, setAmount] = useState('');
  const [desc, setDesc] = useState('');
  const [forHandoff, setForHandoff] = useState(false);
  const [row, setRow] = useState('');
  const h = ctx.handoff && !['CLOSED', 'RETURNED_TO_UMED'].includes(ctx.handoff.state) ? ctx.handoff : null;
  const p = parseDollars(amount);
  const confirm = 'cents' in p && number.trim() && issued ? `Record invoice ${number.trim()} for ${centsToDollars(p.cents)}, issued ${dayWords(issued)}${due ? `, due ${dayWords(due)}` : ''}, for ${ctx.resident.name}?` : null;
  return (
    <FormShell label="Record an invoice" confirmText={confirm} submitLabel="Record this invoice" busyLabel="Recording…" onClose={onClose} onDone={onDone}
      build={() => {
        if (!number.trim()) return { error: 'Enter the invoice number.' };
        if (!issued) return { error: 'Enter the day the invoice was issued.' };
        if (!desc.trim()) return { error: 'Say what the invoice is for.' };
        if (!('cents' in p)) return { error: p.error };
        const body = { invoice_number: number.trim(), issued_on: issued, due_on: due || undefined, total_cents: p.cents, description: desc.trim(), handoff_id: forHandoff && h ? h.id : undefined, import_row_id: row.trim() || undefined };
        return { call: (k) => ledgerApi.recordInvoice(ctx.resident.id, body, k), success: (d) => (d && d.replayed ? 'That invoice was already recorded.' : `Invoice ${body.invoice_number} recorded.`) };
      }}>
      <Field label="Invoice number"><input value={number} onChange={(e) => setNumber(e.target.value)} maxLength={60} /></Field>
      <Field label="Amount ($)"><input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="457.00" /></Field>
      <Field label="Issued on"><input type="date" value={issued} onChange={(e) => setIssued(e.target.value)} /></Field>
      <Field label="Due on (optional)"><input type="date" value={due} onChange={(e) => setDue(e.target.value)} /></Field>
      <Field label="What it is for"><input value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={500} /></Field>
      <Field label="Matching import row (optional)"><input value={row} onChange={(e) => setRow(e.target.value)} placeholder="From Payments & invoices, Needs a match" /></Field>
      {h && <label className="lg-check"><input type="checkbox" checked={forHandoff} onChange={(e) => setForHandoff(e.target.checked)} /> <span>This is the invoice for the Finance handoff</span></label>}
    </FormShell>
  );
}

function OpeningForm({ ctx, onClose, onDone }: { ctx: ResidentContext; onClose: () => void; onDone: (m: string) => Promise<void> | void }) {
  const [amount, setAmount] = useState('');
  const [asOf, setAsOf] = useState(todayEastern());
  const [due, setDue] = useState('');
  const [reason, setReason] = useState('');
  const [number, setNumber] = useState('');
  const p = parseDollars(amount);
  const confirm = 'cents' in p && asOf ? `Carry forward ${centsToDollars(p.cents)} owed as of ${dayWords(asOf)} for ${ctx.resident.name}?` : null;
  return (
    <FormShell label="Record an opening balance" confirmText={confirm} submitLabel="Record this opening balance" busyLabel="Recording…" onClose={onClose} onDone={onDone}
      build={() => {
        if (!('cents' in p)) return { error: p.error };
        if (!asOf) return { error: 'Enter the day the balance is as of.' };
        if (!reason.trim()) return { error: 'Say who confirmed it and how.' };
        const body = { amount_cents: p.cents, as_of: asOf, due_on: due || undefined, reason: reason.trim(), invoice_number: number.trim() || undefined };
        return { call: (k) => ledgerApi.recordOpening(ctx.resident.id, body, k), success: (d) => (d && d.replayed ? 'That opening balance was already recorded.' : 'Opening balance recorded.') };
      }}>
      <p className="um-quiet">Carry forward a balance from the Monday era. Say who confirmed it and how (for example: April confirmed $457 owed as of Oct 1 from the signed agreement). The Monday figures stay separate and are never added in.</p>
      <Field label="Amount owed ($)"><input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="457.00" /></Field>
      <Field label="Owed as of"><input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} /></Field>
      <Field label="Due on (optional)"><input type="date" value={due} onChange={(e) => setDue(e.target.value)} /></Field>
      <Field label="Invoice number (optional)"><input value={number} onChange={(e) => setNumber(e.target.value)} maxLength={60} /></Field>
      <Field label="Who confirmed it, and how"><textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={2000} /></Field>
    </FormShell>
  );
}

/** Oldest due first; no due day last. */
export function openCharges(l: AvailableLedger): Charge[] {
  const all = [...(l.opening_balance ? [l.opening_balance] : []), ...l.invoices].filter((c) => c.state === 'OPEN');
  return all.sort((a, b) => (a.due_on || '9999-12-31').localeCompare(b.due_on || '9999-12-31') || a.issued_on.localeCompare(b.issued_on));
}

const methodsOf = (scope: FinanceScope) => (scope.payment_methods && scope.payment_methods.length ? scope.payment_methods : DEFAULT_METHODS);
const withRef = (label: string, ref: string) => `${label.toLowerCase()}${ref.trim() ? ` ${ref.trim().startsWith('#') ? ref.trim() : `#${ref.trim()}`}` : ''}`;

function PaymentForm({ ctx, scope, ledger, onClose, onDone }: { ctx: ResidentContext; scope: FinanceScope; ledger: AvailableLedger; onClose: () => void; onDone: (m: string) => Promise<void> | void }) {
  const open = openCharges(ledger);
  const methods = methodsOf(scope);
  const [amount, setAmount] = useState('');
  const [on, setOn] = useState(todayEastern());
  const [method, setMethod] = useState<PaymentMethod>(methods[0].id);
  const [invoiceId, setInvoiceId] = useState(open[0] ? open[0].id : '');
  const [ref, setRef] = useState('');
  const [memo, setMemo] = useState('');
  const [row, setRow] = useState('');
  const p = parseDollars(amount);
  const target = open.find((c) => c.id === invoiceId);
  const mLabel = (methods.find((m) => m.id === method) || methods[0]).label;
  const confirm = 'cents' in p && on ? `Record ${centsToDollars(p.cents)} received ${dayWords(on)} by ${withRef(mLabel, ref)} on ${target ? (target.kind === 'OPENING_BALANCE' ? 'the opening balance' : `invoice ${target.invoice_number || ''}`.trim()) : 'the oldest open charge'}?` : null;
  return (
    <FormShell label="Record a payment" confirmText={confirm} submitLabel="Record this payment" busyLabel="Recording…" onClose={onClose} onDone={onDone}
      build={() => {
        if (!('cents' in p)) return { error: p.error };
        if (!on) return { error: 'Enter the day the payment was received.' };
        const body = { amount_cents: p.cents, received_on: on, method, invoice_id: invoiceId || undefined, reference: ref.trim() || undefined, memo: memo.trim() || undefined, import_row_id: row.trim() || undefined };
        return {
          call: (k, dup) => ledgerApi.recordPayment(ctx.resident.id, dup ? { ...body, confirm_duplicate: true } : body, k),
          success: (d) => (d && d.replayed ? 'That payment was already recorded.' : `Payment of ${centsToDollars(body.amount_cents)} recorded${d && d.invoice_paid ? '; that charge is now paid in full' : ''}.`),
        };
      }}>
      {open.length === 0 && <p className="um-quiet">Nothing is open to apply a payment to. Record an invoice or an opening balance first.</p>}
      <Field label="Amount received ($)"><input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="457.00" /></Field>
      <Field label="Received on"><input type="date" value={on} onChange={(e) => setOn(e.target.value)} /></Field>
      <Field label="How it was paid">
        <select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>{methods.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}</select>
      </Field>
      <Field label="Apply to">
        <select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}>
          {open.map((c) => <option key={c.id} value={c.id}>{chargeName(c)} · {centsToDollars(c.open_cents)} open{c.due_on ? ` · due ${dayWords(c.due_on)}` : ''}</option>)}
        </select>
      </Field>
      <Field label="Check number or reference (optional)"><input value={ref} onChange={(e) => setRef(e.target.value)} maxLength={120} /></Field>
      <Field label="Memo (optional)"><input value={memo} onChange={(e) => setMemo(e.target.value)} maxLength={500} /></Field>
      <Field label="Matching import row (optional)"><input value={row} onChange={(e) => setRow(e.target.value)} placeholder="From Payments & invoices, Needs a match" /></Field>
    </FormShell>
  );
}

const HANDOFF_WORD: Record<string, string> = {
  RECEIVED: 'received but not accepted', ACCEPTED: 'accepted', INVOICED: 'invoiced', TERMS_RECORDED: 'terms recorded',
  COLLECTING: 'collecting', PAID_IN_FULL: 'marked paid in full but not closed',
};

/**
 * What "Paid in full — close" will do for THIS resident, in plain words, before the confirm button (ruled 2026-10-20:
 * one step for a payment made outside eMerge). The server decides again; these words mirror ledgerService.paidInFullClose.
 */
export function closePlan(ctx: ResidentContext, ledger: AvailableLedger): { handoff: string; settle: string | null; blocked: boolean } {
  const h = ctx.handoff && !['CLOSED', 'RETURNED_TO_UMED'].includes(ctx.handoff.state) ? ctx.handoff : null;
  const live = (ledger.invoices || []).filter((i) => i.state !== 'VOID');
  const settle = live.length === 0
    ? 'No invoice is recorded for this resident: enter the amount they paid. eMerge will record a one-line "balance settled" entry for that amount and the payment against it, so the balance ends at $0.00. No invoice is invented.'
    : ledger.balance_cents === 0
      ? 'Everything recorded for this resident is already paid: close with $0.00; no payment is recorded.'
      : null;
  if (!h) return { handoff: 'This resident has no open Finance handoff, so there is nothing to close: the payment and a closing note are recorded.', settle, blocked: false };
  if (h.state === 'RECEIVED') return { handoff: 'The Finance handoff has only been received. Accept it first (Handoff work); this cannot close it yet.', settle, blocked: true };
  const word = HANDOFF_WORD[h.state] || h.state.replace(/_/g, ' ').toLowerCase();
  return { handoff: `The Finance handoff is ${word}. This records the payment and moves the handoff to closed, paid in full, in one step.`, settle, blocked: false };
}

export function CloseForm({ ctx, scope, ledger, onClose, onDone }: { ctx: ResidentContext; scope: FinanceScope; ledger: AvailableLedger; onClose: () => void; onDone: (m: string) => Promise<void> | void }) {
  const methods = methodsOf(scope);
  const h = ctx.handoff && !['CLOSED', 'RETURNED_TO_UMED'].includes(ctx.handoff.state) ? ctx.handoff : null;
  // Verifier, 2026-10-20: never default to $0.00. The balance owed, else empty (the person types what was paid).
  const [amount, setAmount] = useState(ledger.balance_cents > 0 ? centsToField(ledger.balance_cents) : '');
  const [on, setOn] = useState(todayEastern());
  const [method, setMethod] = useState<PaymentMethod>(methods[0].id);
  const [ref, setRef] = useState('');
  const [reason, setReason] = useState('');
  // $0.00 only when the resident HAS charges and all are paid (the server's rule); anything else is parsed strictly.
  const allPaid = (ledger.invoices || []).some((i) => i.state !== 'VOID') && ledger.balance_cents === 0;
  const p: { cents: number } | { error: string } = allPaid && amount.trim() !== '' && /^\$?\s*0*(\.0{0,2})?$/.test(amount.trim()) ? { cents: 0 } : parseDollars(amount);
  const mLabel = (methods.find((m) => m.id === method) || methods[0]).label;
  const plan = closePlan(ctx, ledger);
  const confirm = 'cents' in p && on && !plan.blocked ? `Record ${centsToDollars(p.cents)} received ${dayWords(on)} by ${withRef(mLabel, ref)} and close ${ctx.resident.name} as paid in full?${h ? ' This also closes the Finance handoff.' : ' There is no Finance handoff to close.'}` : null;
  const a = ctx.account;
  const mondayLine = a && a.remaining_amount !== null && a.remaining_amount !== undefined
    ? `${a.historical ? a.historical.label : 'Monday historical mirror'}: ${centsToDollars(Math.round(Number(a.remaining_amount) * 100))} remaining — history, not the live balance.`
    : null;
  return (
    <FormShell label="Paid in full — close" confirmText={confirm} submitLabel="Close as paid in full" busyLabel="Closing…" onClose={onClose} onDone={onDone}
      build={() => {
        if (!('cents' in p)) return { error: p.error };
        if (!on) return { error: 'Enter the day the payment was received.' };
        if (plan.blocked) return { error: 'Accept the Finance handoff first (Handoff work); then close it here.' };
        if (!reason.trim()) return { error: 'Say how you know the resident has paid everything.' };
        const body = { amount_cents: p.cents, received_on: on, method, reference: ref.trim() || undefined, reason: reason.trim(), handoff_id: h ? h.id : undefined, handoff_version: h ? h.version : undefined };
        return { call: (k) => ledgerApi.paidInFullClose(ctx.resident.id, body, k), success: (d) => (d && d.replayed ? 'That close was already recorded.' : 'Closed as paid in full.') };
      }}>
      <p className="um-quiet">Use this when the resident has paid everything they owe: it records the payment and closes Finance&rsquo;s work. (&ldquo;Mark paid in full (UMED pending)&rdquo; on the UMED case is different: it only notes the payment while the UMED goes on.)</p>
      <div className="lg-plan" role="note" data-testid="pif-plan">
        <p><strong>What happens:</strong> {plan.handoff}</p>
        {plan.settle && <p>{plan.settle}</p>}
      </div>
      <Field label="Amount received ($)"><input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="457.00" /></Field>
      <p className="um-quiet" data-testid="pif-balances">
        eMerge balance owed now: {centsToDollars(ledger.balance_cents)}.{mondayLine ? ` ${mondayLine}` : ' No Monday figure is on record.'}
      </p>
      <Field label="Received on"><input type="date" value={on} onChange={(e) => setOn(e.target.value)} /></Field>
      <Field label="How it was paid">
        <select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>{methods.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}</select>
      </Field>
      <Field label="Check number or reference (optional)"><input value={ref} onChange={(e) => setRef(e.target.value)} maxLength={60} /></Field>
      <Field label="Why this is paid in full"><textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} /></Field>
    </FormShell>
  );
}

function VoidForm({ kind, name, amountCents, onCall, onClose, onDone }: {
  kind: 'invoice' | 'payment'; name: string; amountCents: number; onCall: (reason: string, key: string) => Promise<ApiResultLike>; onClose: () => void; onDone: (m: string) => Promise<void> | void;
}) {
  const [reason, setReason] = useState('');
  return (
    <FormShell label={`Void a ${kind}`} confirmText={reason.trim() ? `Void ${name} (${centsToDollars(amountCents)})? It stays on the record as voided, with this reason.` : null}
      submitLabel={`Void this ${kind}`} busyLabel="Voiding…" onClose={onClose} onDone={onDone}
      build={() => {
        if (!reason.trim()) return { error: 'Say why it is being voided.' };
        return {
          call: (k) => onCall(reason.trim(), k),
          success: (d) => {
            const w: string[] = d && Array.isArray(d.warnings) ? d.warnings : [];
            return `${d && d.replayed ? `That ${kind} was already voided.` : `${name} voided.`}${w.length ? ` ${w.join(' ')}` : ''}`;
          },
        };
      }}>
      <Field label={`Why this ${kind} is voided`}><textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={2000} /></Field>
    </FormShell>
  );
}

export default function LedgerBilling({ ctx, scope, onChanged }: { ctx: ResidentContext; scope: FinanceScope; onChanged: (msg: string) => Promise<void> }) {
  const [open, setOpen] = useState<Open | null>(null);
  const l = ctx.ledger;
  if (!l) return null;
  const canInvoice = scope.writes === 'NATIVE' && Boolean(scope.can?.record_invoices);
  const canPay = scope.writes === 'NATIVE' && Boolean(scope.can?.record_payments);
  const done = async (msg: string) => { setOpen(null); await onChanged(msg); };
  return (
    <div className="um-section lg-billing" aria-label="Billing">
      <h3>Billing (eMerge ledger — the live balance)</h3>
      {!l.available ? <p className="um-quiet">{l.why}</p> : <Ledger l={l} />}
      {l.available && l.closed_paid_in_full && (
        <p className="lg-closed" role="status">Closed as paid in full on {shortDate(l.closed_paid_in_full.at)}{l.closed_paid_in_full.by_name ? ` by ${l.closed_paid_in_full.by_name}` : ''}{l.closed_paid_in_full.note ? `: ${l.closed_paid_in_full.note}` : ''}</p>
      )}
      {l.available && (
        <>
          <Charges l={l} canVoid={canInvoice} onVoid={(c) => setOpen({ k: 'void-invoice', charge: c })} />
          <Payments l={l} canVoid={canPay} onVoid={(p) => setOpen({ k: 'void-payment', payment: p })} />
        </>
      )}
      {open && open.k === 'invoice' && <InvoiceForm ctx={ctx} onClose={() => setOpen(null)} onDone={done} />}
      {open && open.k === 'opening' && <OpeningForm ctx={ctx} onClose={() => setOpen(null)} onDone={done} />}
      {open && open.k === 'payment' && l.available && <PaymentForm ctx={ctx} scope={scope} ledger={l} onClose={() => setOpen(null)} onDone={done} />}
      {open && open.k === 'close' && l.available && <CloseForm ctx={ctx} scope={scope} ledger={l} onClose={() => setOpen(null)} onDone={done} />}
      {open && open.k === 'void-invoice' && (
        <VoidForm kind="invoice" name={chargeName(open.charge)} amountCents={open.charge.total_cents} onClose={() => setOpen(null)} onDone={done}
          onCall={(reason, key) => ledgerApi.voidInvoice((open as { charge: Charge }).charge.id, reason, key)} />
      )}
      {open && open.k === 'void-payment' && (
        <VoidForm kind="payment" name={`The ${centsToDollars(open.payment.amount_cents)} payment of ${dayWords(open.payment.received_on)}`} amountCents={open.payment.amount_cents} onClose={() => setOpen(null)} onDone={done}
          onCall={(reason, key) => ledgerApi.voidPayment((open as { payment: Payment }).payment.id, reason, key)} />
      )}
      {!open && l.available && (canInvoice || canPay) && (
        <div className="btns lg-acts">
          {canInvoice && <button type="button" className="um-open" onClick={() => setOpen({ k: 'invoice' })}>Record invoice</button>}
          {canInvoice && <button type="button" className="um-open" onClick={() => setOpen({ k: 'opening' })}>Record opening balance</button>}
          {canPay && <button type="button" className="um-open" onClick={() => setOpen({ k: 'payment' })}>Record payment</button>}
          {canPay && <button type="button" className="um-open" onClick={() => setOpen({ k: 'close' })}>Paid in full — close</button>}
        </div>
      )}
    </div>
  );
}

function Ledger({ l }: { l: AvailableLedger }) {
  const a = l.aging;
  return (
    <>
      <dl className="um-kv lg-totals">
        <dt>Balance owed</dt><dd className="lg-balance">{centsToDollars(l.balance_cents)}<span className="fn-source"> · {l.source}</span></dd>
        <dt>Invoiced</dt><dd>{centsToDollars(l.invoiced_cents)}</dd>
        <dt>Paid</dt><dd>{centsToDollars(l.paid_cents)}</dd>
        <dt>Aging</dt>
        <dd>current {centsToDollars(a.current)} · 1–30 {centsToDollars(a.d1_30)} · 31–60 {centsToDollars(a.d31_60)} · 61–90 {centsToDollars(a.d61_90)} · 90+ {centsToDollars(a.d90_plus)}<span className="fn-source"> · as of {shortDate(a.as_of_day)}</span></dd>
      </dl>
    </>
  );
}

function Charges({ l, canVoid, onVoid }: { l: AvailableLedger; canVoid: boolean; onVoid: (c: Charge) => void }) {
  const rows = [...(l.opening_balance ? [l.opening_balance] : []), ...l.invoices];
  if (!rows.length) return <p className="um-quiet">No invoice or opening balance is recorded in eMerge yet.</p>;
  return (
    <div className="lg-tablewrap">
      <StateLegend items={CHARGE_LEGEND} label="Invoice states" />
      <table className="lg-table" aria-label="Invoices">
        <thead><tr><th>Invoice</th><th>Issued</th><th>Due</th><th className="num">Total</th><th className="num">Paid</th><th className="num">Open</th><th>State</th><th>Recorded by</th>{canVoid && <th><span className="lg-sr">Actions</span></th>}</tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className={c.state === 'VOID' ? 'lg-void' : ''}>
              <td>{chargeName(c)}{c.description ? <span className="fn-source"> · {c.description}</span> : null}{c.monday_snapshot ? <span className="fn-source"> · Monday snapshot: {c.monday_snapshot.note || 'kept for reference, not added in'}</span> : null}</td>
              <td>{shortDate(c.issued_on)}</td><td>{shortDate(c.due_on)}</td>
              <td className="num">{centsToDollars(c.total_cents)}</td><td className="num">{centsToDollars(c.paid_cents)}</td><td className="num">{centsToDollars(c.open_cents)}</td>
              <td><span className={`lg-chip lg-chip--${c.state.toLowerCase()}`}>{CHARGE_WORD[c.state]}</span>
                {c.void && <div className="fn-source">Voided {shortDate(c.void.voided_at)}{c.void.voided_by_name ? ` by ${c.void.voided_by_name}` : ''}: {c.void.reason}</div>}</td>
              <td>{c.recorded_by_name || '—'}<span className="fn-source"> · {shortDate(c.recorded_at)}</span></td>
              {canVoid && <td>{c.state !== 'VOID' && <button type="button" className="um-link" onClick={() => onVoid(c)} aria-label={`Void ${chargeName(c)}`}>Void…</button>}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Payments({ l, canVoid, onVoid }: { l: AvailableLedger; canVoid: boolean; onVoid: (p: Payment) => void }) {
  if (!l.payments.length) return <p className="um-quiet">No payment is recorded in eMerge yet.</p>;
  return (
    <div className="lg-tablewrap">
      <StateLegend items={PAYMENT_LEGEND} label="Payment states" />
      <table className="lg-table" aria-label="Payments">
        <thead><tr><th>Received</th><th className="num">Amount</th><th>Method</th><th>Reference</th><th>Applied to</th><th>State</th><th>Recorded by</th>{canVoid && <th><span className="lg-sr">Actions</span></th>}</tr></thead>
        <tbody>
          {l.payments.map((p) => (
            <tr key={p.id} className={p.state === 'VOIDED' ? 'lg-void' : ''}>
              <td>{shortDate(p.received_on)}</td><td className="num">{centsToDollars(p.amount_cents)}</td><td>{p.method_label}</td>
              <td>{p.reference || '—'}{p.source === 'IMPORT' ? <span className="fn-source"> · imported</span> : p.source === 'PAID_IN_FULL_CLOSE' ? <span className="fn-source"> · paid in full close</span> : null}</td>
              <td>{p.invoice_kind === 'OPENING_BALANCE' ? 'Opening balance' : (p.invoice_number || '—')}</td>
              <td><span className={`lg-chip lg-chip--${p.state.toLowerCase()}`}>{p.state === 'VOIDED' ? 'Voided' : 'Recorded'}</span>
                {p.void && <div className="fn-source">Voided {shortDate(p.void.voided_at)}{p.void.voided_by_name ? ` by ${p.void.voided_by_name}` : ''}: {p.void.reason}</div>}</td>
              <td>{p.recorded_by_name || '—'}<span className="fn-source"> · {shortDate(p.recorded_at)}</span></td>
              {canVoid && <td>{p.state === 'RECORDED' && <button type="button" className="um-link" onClick={() => onVoid(p)} aria-label={`Void the ${centsToDollars(p.amount_cents)} payment of ${shortDate(p.received_on)}`}>Void…</button>}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
